import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, ShieldCheck, Sparkles } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo, organizationSchema } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { DynIcon } from "@/components/DynIcon";
import { SERVICE_MODULES, OUTCOMES } from "@/lib/mockData";

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Business Payment Solutions",
  provider: { "@type": "Organization", name: "Elikya Technology" },
  areaServed: ["South Africa", "Democratic Republic of the Congo"],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Elikya Payment Solutions",
    itemListElement: SERVICE_MODULES.map((m) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: m.title, description: m.desc },
    })),
  },
};

// Business-focused interactive showcase (no code)
const SHOWCASE = [
  {
    key: "accept",
    label: "Accept Payments",
    title: "Every method your customers already use",
    body: "Cards, mobile money and bank transfers in one checkout — automatically routed for the highest approval rate.",
    metrics: [
      { k: "28+", v: "payment methods" },
      { k: "99.2%", v: "authorisation rate" },
      { k: "1-click", v: "returning customers" },
    ],
  },
  {
    key: "grow",
    label: "Grow Revenue",
    title: "Turn more visitors into paying customers",
    body: "Optimised checkout, smart retries and local payment options lift conversion across every market you serve.",
    metrics: [
      { k: "+38%", v: "checkout conversion" },
      { k: "–61%", v: "failed payments" },
      { k: "6 days", v: "to first revenue" },
    ],
  },
  {
    key: "manage",
    label: "Manage Money",
    title: "Reconciliation and invoicing on autopilot",
    body: "A real-time ledger balances your books automatically, while branded invoices get paid and matched for you.",
    metrics: [
      { k: "T+0", v: "settlement speed" },
      { k: "0", v: "manual reconciliation" },
      { k: "100%", v: "audit-ready records" },
    ],
  },
];

const Services = () => {
  const [tab, setTab] = useState("accept");
  const active = SHOWCASE.find((s) => s.key === tab);

  return (
    <Layout>
      <Seo
        title="Payment Solutions Built for Real Business Growth"
        description="From local merchants to high-growth enterprises, Elikya provides turnkey payment systems, bank and mobile money integration, bank-grade security and always-on infrastructure."
        keywords="merchant payment gateway africa, enterprise payment infrastructure, omnichannel payment platform africa, b2b payment solutions africa, multi-currency settlement africa, automated business billing platform, pci dss compliant payment provider africa, mobile money integration hub, ussd payment gateway, card processing hub africa, high-availability payment rails, financial ledger engine, double-entry balance engine, girdercore ledger engine, transaction reconciliation engine, core banking ledger integration, elikya technology"
        path="/services"
        ogImage="/og-services.png"
        jsonLd={[organizationSchema, serviceSchema]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stroke">
        <div className="pointer-events-none absolute inset-0 grid-dots opacity-60" />
        <div className="pointer-events-none absolute -top-20 right-[-8%] h-96 w-96 rounded-full bg-brand-tint blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28">
          <Reveal className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-stroke bg-white px-3.5 py-1.5 text-xs font-semibold text-brand">
              <ShieldCheck className="h-3.5 w-3.5" /> Solutions for every business
            </span>
            <h1 className="mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Payment Solutions Built for{" "}
              <span className="text-gradient-brand">Real Business Growth</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slatey sm:text-lg">
              From local merchants to high-growth enterprises, we provide the financial tools that turn
              complex payments into simple, everyday commerce.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MODULES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-24">
        <div className="grid gap-5 md:grid-cols-2">
          {SERVICE_MODULES.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.06}>
              <article
                id={`solution-${m.slug}`}
                data-testid={`module-${m.id}`}
                className="group relative h-full scroll-mt-28 overflow-hidden rounded-2xl border border-stroke bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-float"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-tint text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                    <DynIcon name={m.icon} className="h-5.5 w-5.5" />
                  </div>
                  <span className="rounded-lg bg-brand px-2.5 py-1 text-xs font-bold text-white">
                    Module {m.id}
                  </span>
                </div>
                <h2 className="mt-5 text-xl font-bold text-ink">{m.title}</h2>
                <p className="mt-2.5 text-sm leading-relaxed text-slatey">{m.desc}</p>
                <ul className="mt-5 grid grid-cols-2 gap-2.5 border-t border-stroke pt-5">
                  {m.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-ink">
                      <Check className="h-4 w-4 shrink-0 text-brand" />
                      {p}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/solutions/${m.slug}`}
                  data-testid={`module-learn-more-${m.id}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand transition-colors hover:text-brand-hover"
                >
                  Learn more
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INTERACTIVE BUSINESS SHOWCASE */}
      <section className="border-t border-stroke bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
              <Sparkles className="h-4 w-4" /> What it means for your business
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Real outcomes, not just features
            </h2>
          </Reveal>

          <div className="mt-10 grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
            <div className="flex flex-col gap-2.5" role="tablist" aria-label="Business outcomes">
              {SHOWCASE.map((s) => (
                <button
                  key={s.key}
                  role="tab"
                  aria-selected={tab === s.key}
                  data-testid={`showcase-tab-${s.key}`}
                  onClick={() => setTab(s.key)}
                  className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left transition-all duration-200 ${
                    tab === s.key
                      ? "border-brand bg-brand-tint shadow-sm"
                      : "border-stroke bg-canvas hover:border-brand/40"
                  }`}
                >
                  <span className="font-semibold text-ink">{s.label}</span>
                  <ArrowRight
                    className={`h-4 w-4 transition-all ${
                      tab === s.key ? "text-brand translate-x-0" : "text-slatey -translate-x-1 opacity-0"
                    }`}
                  />
                </button>
              ))}
            </div>

            <div className="rounded-2xl border border-stroke bg-canvas p-7">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-2xl font-bold tracking-tight text-ink">{active.title}</h3>
                  <p className="mt-3 max-w-xl text-base leading-relaxed text-slatey">{active.body}</p>
                  <div className="mt-7 grid gap-4 sm:grid-cols-3">
                    {active.metrics.map((mt) => (
                      <div key={mt.v} className="rounded-xl border border-stroke bg-white p-5 shadow-sm">
                        <p className="text-3xl font-bold tracking-tight text-brand tabular-nums">{mt.k}</p>
                        <p className="mt-1 text-sm text-slatey">{mt.v}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* OUTCOMES BAND */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {OUTCOMES.map((o, i) => (
            <Reveal key={o.label} delay={i * 0.06}>
              <div className="rounded-2xl border border-stroke bg-white p-6 text-center shadow-sm">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-brand-tint text-brand">
                  <DynIcon name={o.icon} className="h-5 w-5" />
                </div>
                <p className="mt-4 text-3xl font-bold tracking-tight text-ink tabular-nums">{o.stat}</p>
                <p className="mt-1 text-sm text-slatey">{o.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="flex flex-col items-center justify-between gap-6 rounded-3xl border border-stroke bg-white px-8 py-10 text-center shadow-float sm:flex-row sm:text-left">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-ink">Ready to grow with better payments?</h2>
              <p className="mt-2 text-sm text-slatey">Create a business account or talk to our payments team.</p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white hover:bg-brand-hover"
              >
                Get Started <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl border border-stroke px-5 py-3 text-sm font-semibold text-ink hover:bg-brand-tint"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </Layout>
  );
};

export default Services;
